import { denoisingModes, advancedSettingsDefaults, voiceAgentActionTypes, timezoneHandlingTypes } from "@/src/constants/voiceAgentConstants.js";
import { msToMinutes, msToSeconds, minutesToMs, secondsToMs } from "@/src/utils/dateTimeUtils.js";
import { capitalizeFirstLetter} from "@/src/utils/utils.js";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";
import store from "@/src/store/index.js";
import retellDevice from "@/src/services/telephony/providers/retell/retellDevice";
import retellEventHandler from "@/src/services/telephony/providers/retell/retellEventHandler";
import { fetchCampaigns, toGetPhoneNumbers } from "@/src/utils/callsUtils.js";
import { dialerTypes } from "@/src/constants/callConstants.js";
import { operatorsWithoutValue } from "@/src/constants/analyticsFilterConstants.js";
    

export const buildFiltersArray = (filterData) => {
  try {
    if (!filterData || typeof filterData !== "object") {
      return [];
    }
    
    return Object.entries(filterData).reduce((filters, [key, value]) => {
      if (value === null || value === undefined || value === "") {
        return filters;
      }
      
      const isObject = typeof value === "object" && !Array.isArray(value);
      const hasOperator = isObject && value.operator !== undefined;
      const hasDateRange = !!(isObject && value.start_date && value.end_date);
      const hasRange = !!(isObject && value.start && value.end);
      
      switch (true) {
        case hasOperator: {
          const filter = { field: key, operator: value.operator };
          
          if (value.operator === "between" && value.value !== null && value.value !== undefined && typeof value.value === "object" && !Array.isArray(value.value)) {
            const { start, end } = value.value;
            const hasValidRange = start !== undefined && start !== null && start !== "" && 
                                  end !== undefined && end !== null && end !== "";
            if (hasValidRange) {
              filter.values = [start, end];
              filters.push(filter);
            }
          } else if (value.value !== null && value.value !== undefined) {
            const values = Array.isArray(value.value) ? value.value : [value.value];
            if (values.length > 0) {
              filter.values = values;
              filters.push(filter);
            }
          } else if (operatorsWithoutValue.includes(value.operator)) {
            filters.push(filter);
          }
          break;
        }
        
        case hasDateRange:
          filters.push({ field: key, values: [value.start_date, value.end_date] });
          break;
        
        case hasRange:
          filters.push({ field: key, values: [value.start, value.end] });
          break;
        
        case Array.isArray(value) && value.length > 0:
          filters.push({ field: key, values: value });
          break;
        
        case typeof value !== "object":
          filters.push({ field: key, values: [value] });
          break;
      }

      return filters;
    }, []);
  } catch (error) {
    console.error("Error building filters array:", error);
    return [];
  }
};


export const extractKnowledgeBaseData = (knowledgeBaseResponse, formData) => {
  try {
    if (!formData) {
      return formData;
    }

    const knowledgeBases = Array.isArray(knowledgeBaseResponse) 
      ? knowledgeBaseResponse 
      : knowledgeBaseResponse?.data || [];

    const transformedItems = [];

    knowledgeBases.forEach((kb) => {
  
      if (kb.knowledge_base_text && kb.knowledge_base_text.trim()) {
        const colonIndex = kb.knowledge_base_text.indexOf(':');
        if (colonIndex !== -1) {
          const title = kb.knowledge_base_text.substring(0, colonIndex).trim();
          const text = kb.knowledge_base_text.substring(colonIndex + 1).trim();
          transformedItems.push({
            id: `${kb.id}-text`,
            kb_id: kb.id,
            type: "text",
            name: title || "Untitled Text",
            text: text || "",
            created_at: kb.created_at,
          });
        } else {
          transformedItems.push({
            id: `${kb.id}-text`,
            kb_id: kb.id,
            type: "text",
            name: kb.knowledge_base_text.trim(),
            text: "",
            created_at: kb.created_at,
          });
        }
      }

      if (kb.knowledge_urls && Array.isArray(kb.knowledge_urls) && kb.knowledge_urls.length > 0) {
        kb.knowledge_urls.forEach((urlString, index) => {
          if (urlString && urlString.trim()) {
            const colonIndex = urlString.indexOf(':');
            if (colonIndex !== -1) {
              const title = urlString.substring(0, colonIndex).trim();
              const url = urlString.substring(colonIndex + 1).trim();
              transformedItems.push({
                id: `${kb.id}-url-${index}`,
                kb_id: kb.id,
                type: "link",
                name: title || "Untitled Link",
                url: url || "",
                created_at: kb.created_at,
              });
            }
          }
        });
      }

      if (kb.knowledge_pdfs && Array.isArray(kb.knowledge_pdfs) && kb.knowledge_pdfs.length > 0) {
        kb.knowledge_pdfs.forEach((pdfString, index) => {
          if (pdfString && pdfString.trim()) {
            const colonIndex = pdfString.indexOf(':');
            if (colonIndex !== -1) {
              const title = pdfString.substring(0, colonIndex).trim();
              const fileUrl = pdfString.substring(colonIndex + 1).trim();
              const fileName = fileUrl ? fileUrl.split('/').pop() : "";
              
              transformedItems.push({
                id: `${kb.id}-pdf-${index}`,
                kb_id: kb.id,
                type: "file",
                name: title || "Untitled File",
                file: {
                  name: fileName || "unknown.pdf",
                  url: fileUrl,
                },
                created_at: kb.created_at,
              });
            }
          }
        });
      }
    });

    formData.knowledge_base_items = transformedItems;

    return formData;
  } catch (error) {
    console.error("Error extracting knowledge base data:", error);
    return formData;
  }
};

export const extractDataExtractionProperties = (dataExtractionsResponse, formData) => {
  try {
    if (!formData) {
      return formData;
    }

    if (dataExtractionsResponse?.success && dataExtractionsResponse?.data) {
      formData.data_extraction_properties = dataExtractionsResponse.data.map(item => ({
        id: item.id,
        name: item.data_name,
        extraction: item.extraction_prompt,
        type: item.extraction_type,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));
    }

    return formData;
  } catch (error) {
    console.error("Error extracting data extraction properties:", error);
    return formData;
  }
};

export const extractActions = (actionsResponse, formData) => {
  try {
    if (!formData) {
      return formData;
    }

    if (actionsResponse && Array.isArray(actionsResponse)) {
      formData.actions = actionsResponse.map(item => {
        const actionDetails = typeof item.action_details === 'string' 
          ? JSON.parse(item.action_details) 
          : item.action_details;

        const action = {
          id: item.id,
          name: item.action_name,
          type: item.action_type,
          created_at: item.created_at,
          updated_at: item.updated_at,
        };

        if (item.action_type === voiceAgentActionTypes.SCHEDULE_MEETING) {
          action.trigger_prompt = actionDetails?.trigger_prompt || "";
          action.meeting_agent = actionDetails?.meeting_agent || "";
          action.meeting_agent_name = actionDetails?.meeting_agent_name || "";
          action.action_timezone = actionDetails?.action_timezone || timezoneHandlingTypes.ASK_USER;
        } else if (item.action_type === voiceAgentActionTypes.QUALIFICATION_AGENT) {
          action.qualification_criteria = actionDetails?.qualification_criteria || "";
        }

        return action;
      });
    }

    return formData;
  } catch (error) {
    console.error("Error extracting actions:", error);
    return formData;
  }
};

export const convertAdvancedSettingsToApiPayload = (formData) => {
  try {
    if (!formData) {
      return {};
    }
    const payload = {};
    
    Object.keys(formData).forEach(key => {
      const value = formData[key];
      
      if (value === undefined || value === null) {
        return;
      }
      
      switch (key) {
        case 'background_sound':
          if (value) {
            payload.background_sound = value;
          }
          break;
          
        case 'smart_noise_cancellation':
          if (value) {
            payload.denoising_mode = denoisingModes.NOISE_CANCELLATION;
          }
          break;
          
        case 'responsiveness':
          payload.responsiveness = value;
          break;
          
        case 'interruption_sensitivity':
          payload.interruption_sensitivity = value;
          break;
          
        case 'maximum_call_duration':
          payload.max_call_duration_ms = minutesToMs(value);
          break;
          
        case 'end_call_on_silence':
          payload.end_call_after_silence_ms = secondsToMs(value);
          break;
          
        case 'pause_before_speaking':
          payload.begin_message_delay_ms = secondsToMs(value);
          break;
          
        case 'follow_up_times':
          payload.reminder_max_count = value;
          break;
          
        case 'follow_up_interval':
          payload.reminder_trigger_ms = secondsToMs(value);
          break;
          
        default:
          break;
      }
    });

    return payload;
  } catch (error) {
    console.error("Error converting advanced settings to API payload:", error);
    return {};
  }
};

export const convertApiResponseToFormData = (apiData, voices, languages) => {
  try {
    if (!apiData) {
      return null;
    }

    const formData = {
      name: apiData.agent_name || "",
      agent_id: apiData.id || null,
      voice: null,
      language: null,
      role_personality: apiData.agent_role || "",
      call_flow_instructions: apiData.agent_instruction || "",
      knowledge_base_text: "",
      actions: [],
      data_extraction_properties: [],
      background_sound: apiData.background_sound || "none",
      smart_noise_cancellation: apiData.denoising_mode === denoisingModes.NOISE_CANCELLATION,
      responsiveness: apiData.responsiveness ?? advancedSettingsDefaults.responsiveness,
      interruption_sensitivity: apiData.interruption_sensitivity ?? advancedSettingsDefaults.interruptionSensitivity,
      maximum_call_duration: msToMinutes(apiData.max_call_duration_ms) || advancedSettingsDefaults.maximumCallDuration,
      end_call_on_silence: msToSeconds(apiData.end_call_after_silence_ms) || advancedSettingsDefaults.endCallOnSilence,
      pause_before_speaking: msToSeconds(apiData.begin_message_delay_ms) ?? advancedSettingsDefaults.pauseBeforeSpeaking,
      follow_up_times: apiData.reminder_max_count || advancedSettingsDefaults.followUpTimes,
      follow_up_interval: msToSeconds(apiData.reminder_trigger_ms) || advancedSettingsDefaults.followUpInterval,
      agent_timezone: apiData.agent_timezone || null,
      is_paused: apiData.is_paused ?? false,
    };

    if (apiData.vendor_voice_id && Array.isArray(voices) && voices.length > 0) {
      formData.voice = voices.find(v => v?.voice_id === apiData.vendor_voice_id) || null;
    }

    if (apiData.vendor_language_id && Array.isArray(languages) && languages.length > 0) {
      formData.language = languages.find(l => l?.voice_id === apiData.vendor_language_id) || null;
    }

    return formData;
  } catch (error) {
    console.error("Error converting API response to form data:", error);
    return null;
  }
};

export const formatAgentName = (name) => {
  try {
    if (!name) return '';
    return name
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => capitalizeFirstLetter(word))
      .join(' ');
  } catch (error) {
    console.error("Error formatting agent name:", error);
    return name;
  }
};

export const fetchVoiceAgents = async (params = {}) => {
  try {
    const { page = 1, pageSize = 20, searchQuery = '', filters = [] } = params;
    const response = await VoiceAgentService.fetchVoiceAgents({ page, pageSize, searchQuery, filters });
    if (response?.success && response?.data) {
      return response.data;
    }
    return [];
  } catch (error) {
    console.error("Error fetching voice agents:", error);
    return [];
  }
};

export const fetchVoices = async () => {
  try {
    const response = await VoiceAgentService.getVoices();
    if (response?.success && response?.voices) {
      return response.voices;
    }
    return [];
  } catch (error) {
    console.error("Error fetching voices:", error);
    return [];
  }
};

export const fetchVoicesForFilters = async (filterOptions) => {
  try {
    const voices = await fetchVoices();
    const voiceFilter = filterOptions.find(f => f.id === "voice");
    if (voiceFilter && voices.length > 0) {
      voiceFilter.options = voices.map(voice => ({
        id: voice.voice_id,
        name: voice.voice_name,
        avatar_url: voice.avatar_url
      }));
    }
    return voices;
  } catch (error) {
    console.error("Error fetching voices for filters:", error);
    return [];
  }
};

export const fetchUsersForFilters = async (filterOptions) => {
  try {
    const users = store.getters["users/users"];
    if (users && Array.isArray(users)) {
      const createdByFilter = filterOptions.find(f => f.id === "created_by");
      if (createdByFilter) {
        createdByFilter.options = users.map(user => ({
          id: user.id,
          name: user.name
        }));
      }
    }
  } catch (error) {
    console.error("Error fetching users for filters:", error);
  }
};

export const fetchVoiceAgentsForFilters = async (filterOptions) => {
  try {
    const agents = await fetchVoiceAgents({ page: 1, pageSize: 20, searchQuery: '', filters: [] });
    const agentFilter = filterOptions.find(f => f.id === "agent_id");
    if (agentFilter && agents && Array.isArray(agents) && agents.length > 0) {
      agentFilter.options = agents.map(agent => ({
        id: agent.id,
        name: agent.agent_name || `Agent ${agent.id}`
      }));
    }
    return agents;
  } catch (error) {
    console.error("Error fetching voice agents for filters:", error);
    return [];
  }
};

export const initializeRetellTestCall = async () => {
  try {
    await retellDevice.initialize(retellEventHandler);
    return { success: true };
  } catch (error) {
    console.error("Failed to initialize Retell test call:", error);
    throw error;
  }
};

export const placeRetellTestCall = async (agentId) => {
  try {
    if (!agentId) {
      throw new Error("Agent ID is required");
    }

    await retellDevice.initialize(retellEventHandler);
    
    store.dispatch("voiceAgents/startTestCall", agentId);
    
    await retellDevice.makeCall({ agentId });

    return { success: true };
  } catch (error) {
    console.error("Failed to place Retell test call:", error);
    store.dispatch("voiceAgents/setCallError", error?.message || "Failed to place call");
    throw error;
  }
};

export const endRetellTestCall = async () => {
  try {
    await retellDevice.hangupCall();
    return { success: true };
  } catch (error) {
    console.error("Failed to end Retell test call:", error);
    throw error;
  }
};

export const cleanupRetellTestCall = async () => {
  try {
    await retellDevice.cleanup();
    return { success: true };
  } catch (error) {
    console.error("Failed to cleanup Retell test call:", error);
    throw error;
  }
};

export const extractCallNumber = (direction, toNumber, fromNumber, numberType) => {
  try {
    if (numberType === 'contact') {
      return direction === 'outbound' ? toNumber : fromNumber;
    } else if (numberType === 'your') {
      return direction === 'outbound' ? fromNumber : toNumber;
    }
    return '';
  } catch (error) {
    console.error("Error extracting call number:", error);
    return '';
  }
};

export const formatDirection = (direction) => {
  try {
    if (!direction) return '';
    return direction === 'inbound' ? 'Incoming' : direction === 'outbound' ? 'Outgoing' : capitalizeFirstLetter(direction);
  } catch (error) {
    console.error("Error formatting direction:", error);
    return direction;
  }
};

export const fetchCampaignsForFilters = async (filterOptions, is_agent_campaign = true, searchQuery = '') => {
  try {
    const campaignType = is_agent_campaign ? dialerTypes.VOICE_AGENTS_DIALER : null;
    const campaigns = await fetchCampaigns({
      page: 1,
      pageSize: 50,
      searchQuery,
      filters: []
    }, campaignType);

    const campaignFilter = filterOptions.find(f => f.id === "campaign_id");
    if (campaignFilter && Array.isArray(campaigns?.campaigns)) {
      const mapped = campaigns.campaigns.map(campaign => ({
        id: campaign.id,
        name: campaign.name || `Campaign ${campaign.id}`
      }));
      campaignFilter.options = mapped;
      campaignFilter.originalOptions = mapped;
    }
    return campaigns?.campaigns || [];
  } catch (error) {
    console.error("Error fetching campaigns for filters:", error);
    return [];
  }
};

export const fetchPhoneNumbersForFilters = async (filterOptions) => {
  try {
    const phoneNumbers = await toGetPhoneNumbers();
    const phoneNumberFilter = filterOptions.find(f => f.id === "phone_number");
    if (phoneNumberFilter && phoneNumbers && Array.isArray(phoneNumbers) && phoneNumbers.length > 0) {
      phoneNumberFilter.options = phoneNumbers.map(number => ({
        id: number.phone_number,
        name: number.phone_number,
      }));
    }
    return phoneNumbers;
  } catch (error) {
    console.error("Error fetching phone numbers for filters:", error);
    return [];
  }
};

export const formatCallAnalysisLabel = (key) => {
  try {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  } catch (error) {
    console.error("Error formatting label:", error);
    return key;
  }
};

export const formatCallAnalysisValue = (value) => {
  try {
    if (typeof value === "object" && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  } catch (error) {
    console.error("Error formatting value:", error);
    return "";
  }
};

export const extractCallAnalysisSections = (data, dataKey) => {
  try {
    if (!data || !dataKey) {
      return [];
    }

    const extractedData = data[dataKey];

    if (!extractedData || typeof extractedData !== "object") {
      return [];
    }

    const sections = [];
    Object.entries(extractedData).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        sections.push({
          label: formatCallAnalysisLabel(key),
          value: formatCallAnalysisValue(value),
        });
      }
    });

    return sections;
  } catch (error) {
    console.error("Error extracting call analysis sections:", error);
    return [];
  }
};

export const getCountryCodeFromLocale = (localeCode) => {
  if (!localeCode || localeCode === 'multi') {
    return null;
  }
  
  const specialMappings = {
    'es-419': 'MX',
    'fil-PH': 'PH',
  };
  
  if (specialMappings[localeCode]) {
    return specialMappings[localeCode];
  }
  
  const parts = localeCode.split('-');
  return parts[parts.length - 1];
};

export const mapLanguagesWithFlags = (languageDetails, countryOptions) => {
  try {
    if (!languageDetails || !countryOptions) {
      return [];
    }

    return Object.values(languageDetails).map((language) => {
      if (language.useLocalFlag) {
        return {
          ...language,
          voice_name: language.name,
        };
      }
      
      const countryCode = getCountryCodeFromLocale(language.locale);
      const country = countryOptions.find(c => c.countryCode === countryCode);
      
      return {
        ...language,
        voice_name: language.name,
        flag: country?.flag || null,
      };
    });
  } catch (error) {
    console.error("Error mapping languages with flags:", error);
    return [];
  }
};

export const fetchDynamicVariablesForAgent = async (agentId, searchQuery = "") => {
  try {
    if (!agentId) {
      return [];
    }
    const response = await VoiceAgentService.getDynamicVariables(
      agentId,
      searchQuery,
      1,
      30
    );

    if (response?.success && response?.variables) {
      return response.variables;
    }
    return [];
  } catch (error) {
    console.error("Error fetching dynamic variables:", error);
    return [];
  }
};

export const buildKnowledgeBaseFormData = (itemsList, agentId, knowledgeBaseName) => {
  try {
    const formData = new FormData();
    
    if (!agentId) {
      throw new Error("Agent ID is required");
    }
    
    formData.append('voice_agent_id', agentId);
    formData.append('knowledge_base_name', knowledgeBaseName || 'Knowledge Base');

    if (!Array.isArray(itemsList)) {
      return formData;
    }

    let newPdfIndex = 0;
    let urlIndex = 0;

    itemsList.forEach((item) => {
      if (!item) return;

      if (item.type === 'text' && item.text) {
        formData.append('knowledge_base_texts[title]', item.name || '');
        formData.append('knowledge_base_texts[text]', item.text || '');
      } else if (item.type === 'link' && item.url) {
        const normalizedUrl = normalizeUrl(item.url);
        formData.append(`knowledge_base_urls[${urlIndex}][title]`, item.name || '');
        formData.append(`knowledge_base_urls[${urlIndex}][url]`, normalizedUrl || '');
        urlIndex++;
      } else if (item.type === 'file' && item.file) {
        if (item.file instanceof File) {
          formData.append(`knowledge_base_pdfs[${newPdfIndex}][title]`, item.name || '');
          formData.append(`knowledge_base_pdfs[${newPdfIndex}][pdf]`, item.file);
          newPdfIndex++;
        } else if (item.file.url) {
          const pdfUrlValue = `${item.name || ''}:${item.file.url}`;
          formData.append('knowledge_base_pdf_urls[]', pdfUrlValue);
        }
      }
    });

    return formData;
  } catch (error) {
    console.error("Error building knowledge base form data:", error);
    throw error;
  }
};

export const normalizeUrl = (url) => {
  if (!url) {
    return url;
  }

  if (!/^https?:\/\//i.test(url)) {
    return `https://${url}`;
  }

  return url;
};

export const validateUrl = (url) => {
  if (!url) {
    return { isValid: true, error: "" };
  }
  const invalid = { isValid: false, error: "Please enter a valid website URL" };
  try {
    const candidate = url.includes("://") ? url : `https://${url}`;
    const parsed = new URL(candidate);
    if (!/^https?:$/.test(parsed.protocol)) return invalid;
    if (!parsed.hostname.includes(".")) return invalid;
    return { isValid: true, error: "" };
  } catch {
    return invalid;
  }
};

